import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import {
  MessageCircle, Send, X, User, Phone, ArrowLeft, Check, CheckCheck
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MessagingPanel = ({ isOpen, onClose, defaultRecipient = null }) => {
  const { user } = useAuth();
  const { companyId } = useCompany();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversations = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await axios.get(`${API}/messages/conversations`, {
        params: { company_id: companyId }
      });
      setConversations(response.data);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    }
  }, [companyId]);

  const fetchMessages = useCallback(async (partnerId) => {
    if (!companyId || !partnerId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/messages`, {
        params: { company_id: companyId, conversation_with: partnerId }
      });
      setMessages(response.data.reverse());
      
      // Mark messages as read
      await axios.put(`${API}/messages/mark-all-read`, null, {
        params: { from_user_id: partnerId, company_id: companyId }
      });
      
      // Refresh conversations to update unread count
      fetchConversations();
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  }, [companyId, fetchConversations]);

  const fetchUsers = useCallback(async () => {
    if (!companyId) return;
    try {
      const response = await axios.get(`${API}/users/contacts`, {
        params: { company_id: companyId }
      });
      // Filter out current user (should already be filtered by backend)
      setUsers(response.data.filter(u => u.id !== user?.id));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [companyId, user?.id]);

  useEffect(() => {
    if (isOpen) {
      fetchConversations();
      fetchUsers();
      
      if (defaultRecipient) {
        setSelectedConversation({
          partner_id: defaultRecipient.id,
          partner_name: defaultRecipient.name,
          partner_role: defaultRecipient.role
        });
      }
    }
  }, [isOpen, fetchConversations, fetchUsers, defaultRecipient]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.partner_id);
    }
  }, [selectedConversation, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Poll for new messages every 10 seconds
  useEffect(() => {
    if (!isOpen || !selectedConversation) return;
    
    const interval = setInterval(() => {
      fetchMessages(selectedConversation.partner_id);
    }, 10000);
    
    return () => clearInterval(interval);
  }, [isOpen, selectedConversation, fetchMessages]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !companyId) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        to_user_id: selectedConversation.partner_id,
        content: newMessage.trim()
      }, { params: { company_id: companyId } });
      
      setNewMessage("");
      fetchMessages(selectedConversation.partner_id);
      toast.success("Message envoyé");
    } catch (error) {
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewConversation = (recipient) => {
    setSelectedConversation({
      partner_id: recipient.id,
      partner_name: recipient.name,
      partner_role: recipient.role
    });
    setShowNewConversation(false);
  };

  const getRoleBadge = (role) => {
    const badges = {
      teleprospector: { label: "Téléprospecteur", className: "bg-blue-100 text-blue-700" },
      sales_rep: { label: "Commercial", className: "bg-emerald-100 text-emerald-700" },
      admin: { label: "Admin", className: "bg-purple-100 text-purple-700" }
    };
    return badges[role] || { label: role, className: "bg-slate-100 text-slate-700" };
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg h-[600px] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-4 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2">
            {selectedConversation ? (
              <>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedConversation(null)}
                  className="p-1 h-auto"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                    <User className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div>
                    <span className="font-semibold">{selectedConversation.partner_name}</span>
                    <Badge className={`ml-2 text-xs ${getRoleBadge(selectedConversation.partner_role).className}`}>
                      {getRoleBadge(selectedConversation.partner_role).label}
                    </Badge>
                  </div>
                </div>
              </>
            ) : (
              <>
                <MessageCircle className="w-5 h-5 text-indigo-600" />
                <span>Messages</span>
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {!selectedConversation ? (
            // Conversation List
            <div className="h-full overflow-y-auto">
              {/* New Conversation Button */}
              <div className="p-4 border-b border-slate-100">
                <Button
                  onClick={() => setShowNewConversation(!showNewConversation)}
                  variant="outline"
                  className="w-full"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Nouvelle conversation
                </Button>
              </div>

              {/* New Conversation - User Selection */}
              {showNewConversation && (
                <div className="p-4 bg-slate-50 border-b border-slate-200">
                  <p className="text-sm text-slate-600 mb-3">Sélectionnez un destinataire :</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {users.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => startNewConversation(u)}
                        className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white transition-colors text-left"
                      >
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{u.name}</p>
                          <Badge className={`text-xs ${getRoleBadge(u.role).className}`}>
                            {getRoleBadge(u.role).label}
                          </Badge>
                        </div>
                      </button>
                    ))}
                    {users.length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">
                        Aucun utilisateur disponible
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Existing Conversations */}
              {conversations.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Aucune conversation</p>
                  <p className="text-sm mt-1">Commencez une nouvelle conversation</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {conversations.map((conv) => (
                    <button
                      key={conv.partner_id}
                      onClick={() => setSelectedConversation(conv)}
                      className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left"
                    >
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-indigo-600" />
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-slate-900 truncate">{conv.partner_name}</p>
                          <span className="text-xs text-slate-400">
                            {format(parseISO(conv.last_message_at), "HH:mm", { locale: fr })}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`text-xs ${getRoleBadge(conv.partner_role).className}`}>
                            {getRoleBadge(conv.partner_role).label}
                          </Badge>
                          <p className="text-sm text-slate-500 truncate">{conv.last_message}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Message Thread
            <div className="h-full flex flex-col">
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {loading ? (
                  <div className="text-center py-8 text-slate-500">Chargement...</div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <MessageCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p>Aucun message</p>
                    <p className="text-sm">Envoyez le premier message !</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isFromMe = msg.from_user_id === user?.id;
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isFromMe ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                            isFromMe
                              ? "bg-indigo-600 text-white rounded-br-md"
                              : "bg-slate-100 text-slate-900 rounded-bl-md"
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <div className={`flex items-center justify-end gap-1 mt-1 ${
                            isFromMe ? "text-indigo-200" : "text-slate-400"
                          }`}>
                            <span className="text-xs">
                              {format(parseISO(msg.created_at), "HH:mm", { locale: fr })}
                            </span>
                            {isFromMe && (
                              msg.read ? (
                                <CheckCheck className="w-3 h-3" />
                              ) : (
                                <Check className="w-3 h-3" />
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-slate-200 bg-white">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Écrivez votre message..."
                    className="flex-1"
                    disabled={sending}
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sending}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MessagingPanel;
